'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { usePageviewId } from '@packages/tracking/src/components/PageviewProvider/PageviewProvider';
import {
  useCookies,
  type CookieOptions,
} from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';

export const appBannerCookieName = 'bannerDisabled';
export const appBannerPageViewsCookieName = 'appBannerPageViews';

const SECONDS_PER_DAY = 86400;

const BASE_COOKIE_OPTIONS: CookieOptions = {
  path: '/',
  secure: false,
  sameSite: 'lax',
  httpOnly: false,
};

const readPageViews = (cookies: Record<string, string>) => {
  const parsed = Number.parseInt(cookies[appBannerPageViewsCookieName] ?? '', 10);

  return Number.isNaN(parsed) ? 0 : parsed;
};

export const useAppBannerVisibility = () => {
  const { getCookies, setCookie } = useCookies();
  const {
    appBanner: { dismissDurationDays, autoHideEnabled, autoHidePageViews },
  } = useConfig();
  const pageviewId = usePageviewId();

  const [isHidden, setIsHidden] = useState(() => getCookies()[appBannerCookieName] === 'true');
  const [pageViews, setPageViews] = useState(() => readPageViews(getCookies()));
  const countedPageviewId = useRef<string>(undefined);

  const hide = useCallback(() => {
    setIsHidden(true);
    setCookie(null, appBannerCookieName, 'true', {
      ...BASE_COOKIE_OPTIONS,
      maxAge: dismissDurationDays * SECONDS_PER_DAY,
    });
  }, [dismissDurationDays, setCookie]);

  useEffect(() => {
    if (!autoHideEnabled || !pageviewId || isHidden || countedPageviewId.current === pageviewId) {
      return;
    }

    countedPageviewId.current = pageviewId;

    const nextPageViews = readPageViews(getCookies()) + 1;

    setCookie(null, appBannerPageViewsCookieName, String(nextPageViews), BASE_COOKIE_OPTIONS);
    setPageViews(nextPageViews);

    if (nextPageViews > autoHidePageViews) {
      hide();
    }
  }, [autoHideEnabled, autoHidePageViews, pageviewId, isHidden, hide, getCookies, setCookie]);

  const isAutoHidden = autoHideEnabled && pageViews > autoHidePageViews;

  return { isVisible: !isHidden && !isAutoHidden, dismiss: hide };
};
